import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FileUpload } from '@frame/ui-components/file-upload'
import { Footer } from '@frame/ui-components/footer'
import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'

import { DragOverlay } from '@documents/components/drag-overlay'
import { useDocumentsFlowContext } from '@documents/contexts/documents-flow-context'
import { useFileUploadContext } from '@documents/contexts/file-upload-context'
import { DocumentTypeOption, FileUploadState } from '@documents/types'
import { FileErrors } from '@documents/v2/components/file-errors'
import { Files } from '@documents/v2/components/files'
import { ConfirmExitUploadPopover } from '@documents/v2/screens/upload-documents/confirm-exit-upload-popover'
import { ConfirmRemoveDocumentPopover } from '@documents/v2/screens/upload-documents/confirm-remove-document-popover'
import { getSubmitDocumentsButtonText } from '@documents/v2/utils/getSubmitDocumentsButtonText'
import { getUploadHeading } from '@documents/v2/utils/getUploadHeading'
import { InternalEventType } from '@frame/lib/api/events/types'
import { buildEmployerHistoryViewProps } from '@frame/lib/api/events/utils'
import { TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useHeaderTitle } from '@frame/lib/hooks/useHeaderTitle'
import { useScrollToBottom } from '@frame/lib/hooks/useScrollToBottom'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { useStrategies } from '@frame/lib/hooks/useStrategies'
import { useStore } from '@frame/lib/stores/useStore'
import { cn } from '@frame/lib/utils/cn'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

import { ConfirmPopover } from './confirm-popover'
import { MoreInfoCard, MoreInfoPopover } from './more-info'

export type SelectedFileToRemove = {
  tempId: string
  fileName: string
  fileId?: string
  state: FileUploadState
}

export function UploadDocuments() {
  const navigate = useNavigate()

  const { mutateAndNavigate } = useAutoNavigate()
  const { api, actions } = useRootContext()
  const { handleEvent } = useEventHandler()
  const { bilEnabledStrategies } = useStrategies()
  const { dragActive, trackedFiles, setTrackedFiles, completedFiles, failedFiles, setShowDropzone, deleteFile } =
    useFileUploadContext()
  const { selectedDocumentType } = useDocumentsFlowContext()
  const isMultiTaskSession = useStore(({ session }) => Boolean((session?.max_employments || 1) > 1))
  const title = useHeaderTitle()
  const showExitPrompt = useShowExitPrompt()
  const directTask = useDirectTask()

  const [isLoading, setIsLoading] = useState(false)
  const [showInfoPopover, setShowInfoPopover] = useState(false)
  const [showConfirmPopover, setShowConfirmPopover] = useState(false)
  const [showRemoveDocumentPopover, setShowRemoveDocumentPopover] = useState(false)
  const [showExitUploadPopover, setShowExitUploadPopover] = useState(false)

  const [selectedFileToRemove, setSelectedFileToRemove] = useState<SelectedFileToRemove | null>(null)

  const isSubmitDisabled =
    isLoading || completedFiles.length === 0 || trackedFiles.some((file) => file.state === FileUploadState.PROCESSING)
  const submitButtonText = getSubmitDocumentsButtonText(completedFiles.length)
  const hideClassification =
    selectedDocumentType === DocumentTypeOption.OTHER || selectedDocumentType === DocumentTypeOption.OFFER_LETTER
  const heading = getUploadHeading(selectedDocumentType)
  const processingFiles = trackedFiles.filter((file) => file.state === FileUploadState.PROCESSING)

  useScrollToBottom({
    isReadyToScroll: processingFiles.length > 0,
    startScroll: trackedFiles.length == completedFiles.length + failedFiles.length,
  })

  useEffect(() => {
    setShowDropzone(true)
  }, [setShowDropzone])

  // Render the remove document popover when there is a selected file to remove
  useEffect(() => {
    setShowRemoveDocumentPopover(selectedFileToRemove !== null)
  }, [selectedFileToRemove])

  // Close popovers on escape key press
  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowConfirmPopover(false)
        setShowInfoPopover(false)
        setShowRemoveDocumentPopover(false)
        setSelectedFileToRemove(null)
      }
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [setShowConfirmPopover, setShowInfoPopover])

  function setSelectedFileToRemoveWrapper(selected: SelectedFileToRemove) {
    setSelectedFileToRemove(selected)
  }

  async function completeSession() {
    await api.clientSession.completeSession()

    handleEvent(
      InternalEventType.SHARED_EMPLOYER_DATA,
      buildEmployerHistoryViewProps(false, bilEnabledStrategies, directTask ? [directTask] : [])
    )

    navigate(routes.complete, { replace: true })
  }

  async function handleSubmit() {
    setIsLoading(true)

    if (directTask) {
      await actions.attachFilesToDirectTask(directTask?.id, {
        file_ids: completedFiles
          .map((f) => f.uploadedData?.uploaded_file_id)
          .filter((id): id is string => typeof id === 'string'),
      })
    }

    const data = await actions.completeDirectTask({
      directTaskId: directTask?.id,
      completeDirectTaskBody: {
        strategy: TrueworkJsStrategy.DOCUMENTS,
      },
      multiEmployer: isMultiTaskSession,
    })

    if (!data) {
      navigate(routes.error)
      return
    }

    if (isMultiTaskSession) {
      mutateAndNavigate()
    } else {
      completeSession()
    }
  }

  function handleOpenMoreInfoPopover() {
    setShowInfoPopover(true)
  }

  function handleHideMoreInfoPopover() {
    setShowInfoPopover(false)
  }

  function handleOpenConfirmPopover() {
    setShowConfirmPopover(true)
  }

  function handleHideConfirmPopover() {
    setShowConfirmPopover(false)
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        title={title}
        showExitPrompt={showExitPrompt}
        backButton={{
          onClick: () => {
            if (trackedFiles.length !== 0) {
              setShowExitUploadPopover(true)
            } else {
              navigate(-1)
            }
          },
        }}
      />
      <DragOverlay isVisible={dragActive} />
      <FileErrors />
      <Body
        data-cy={DataAttribute.DOCUMENTS_SCREEN_UPLOAD_DOCUMENTS}
        className={cn('flex h-full flex-col p-5 pb-2', isLoading && 'pointer-events-none')}
        paddingTopOverride={0}
      >
        <H2 className="mb-4 text-left text-moss-700 first-letter:capitalize">{heading}</H2>
        <div className="flex h-full flex-col gap-3">
          <MoreInfoCard onClick={handleOpenMoreInfoPopover} />
          <Files
            files={trackedFiles}
            hideClassification={hideClassification}
            setSelectedFileToRemove={setSelectedFileToRemoveWrapper}
          />
          <FileUpload />
        </div>
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button
              Icon={isLoading ? LoadingSpinner : undefined}
              disabled={isSubmitDisabled}
              onClick={handleOpenConfirmPopover}
            >
              {submitButtonText}
            </Button>
          ),
        }}
      />
      <ConfirmPopover
        isOpen={showConfirmPopover}
        onClose={handleHideConfirmPopover}
        onConfirm={handleSubmit}
        confirmText={submitButtonText}
        isLoading={isLoading}
      />
      <ConfirmRemoveDocumentPopover
        isOpen={showRemoveDocumentPopover && selectedFileToRemove?.state !== FileUploadState.FAILED}
        onClose={() => {
          setSelectedFileToRemove(null)
          setShowRemoveDocumentPopover(false)
        }}
        onRemove={() => {
          if (selectedFileToRemove !== null) {
            deleteFile({ tempId: selectedFileToRemove?.tempId, fileId: selectedFileToRemove?.fileId })
            setSelectedFileToRemove(null)
            setShowRemoveDocumentPopover(false)
          }
        }}
        onCancel={() => {
          setSelectedFileToRemove(null)
          setShowRemoveDocumentPopover(false)
        }}
        title={`Are you sure you want to remove ${selectedFileToRemove?.fileName}?`}
      />
      <MoreInfoPopover isOpen={showInfoPopover} onClose={handleHideMoreInfoPopover} />
      <ConfirmExitUploadPopover
        isOpen={showExitUploadPopover}
        onClose={() => setShowExitUploadPopover(false)}
        onConfirmExit={() => {
          setTrackedFiles([])
          setShowExitUploadPopover(false)
          navigate(-1)
        }}
        onCancel={() => setShowExitUploadPopover(false)}
      />
    </>
  )
}
